// AirportAutocomplete — debounced search against /airports/search.
// Keyboard-navigable dropdown with IATA code + city name.

import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface AirportOption {
  iata: string;
  name: string;
  city: string;
  country: string;
}

interface AirportSearchResponse {
  data?: {
    airports?: AirportOption[];
  };
}

interface AirportAutocompleteProps {
  label: string;
  placeholder?: string;
  value: AirportOption | null;
  onChange: (airport: AirportOption | null) => void;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debounced;
}

export function AirportAutocomplete({
  label,
  placeholder = 'City or airport',
  value,
  onChange,
}: AirportAutocompleteProps) {
  const [query, setQuery] = useState(value ? `${value.city} (${value.iata})` : '');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const debouncedQuery = useDebounce(query, 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['airports', debouncedQuery],
    queryFn: async () => {
      if (debouncedQuery.length < 2) return { airports: [] as AirportOption[] };
      const res = await api.get<AirportSearchResponse>('/airports/search', {
        params: { q: debouncedQuery, limit: 6 },
      });
      return { airports: res.data.data?.airports ?? [] };
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 60_000,
  });

  const airports = data?.airports ?? [];

  useEffect(() => {
    setCursor(0);
  }, [airports]);

  // Close on outside click
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const select = useCallback((a: AirportOption) => {
    onChange(a);
    setQuery(`${a.city} (${a.iata})`);
    setOpen(false);
  }, [onChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, airports.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    if (e.key === 'Enter' && airports[cursor]) { select(airports[cursor]); }
    if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1 }}>
      <div style={{
        padding: '14px 18px',
        background: '#131C30',
      }}>
        <div style={{
          fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
          fontSize: 10, color: '#8C98B0', letterSpacing: 1.5, fontWeight: 600,
          textTransform: 'uppercase', marginBottom: 4,
        }}>{label}</div>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (!e.target.value) onChange(null);
          }}
          onFocus={() => { setOpen(query.length >= 2); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{
            background: 'transparent',
            border: 'none',
            outline: 'none',
            fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
            fontSize: 16, fontWeight: 600, color: '#F4F3F0',
            width: '100%',
            caretColor: '#E8441A',
          }}
        />
      </div>

      {/* Dropdown */}
      {open && (airports.length > 0 || isLoading) && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          minWidth: 280,
          zIndex: 200,
          background: '#131C30',
          border: '1px solid rgba(255,255,255,0.14)',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}>
          {isLoading ? (
            <div style={{ padding: '14px 18px', color: '#8C98B0', fontSize: 13 }}>Searching…</div>
          ) : airports.map((a, i) => (
            <button
              key={a.iata}
              onMouseDown={() => select(a)}
              style={{
                width: '100%',
                padding: '12px 18px',
                background: cursor === i ? 'rgba(255,255,255,0.06)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                textAlign: 'left',
                color: '#F4F3F0',
                transition: 'background .1s',
              }}
              onMouseEnter={() => setCursor(i)}
            >
              <span style={{
                fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
                fontSize: 15, fontWeight: 700, color: '#E8441A', flexShrink: 0,
              }}>{a.iata}</span>
              <div>
                <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 14, fontWeight: 600 }}>{a.city}</div>
                <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 11, color: '#8C98B0' }}>{a.name} · {a.country}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
